<template>
  <v-container
    fluid
    fill-height
    class="py-0"
  >
    <template-preview-dialog ref="templatePreview" />
    <v-row>
      <v-col>
        <account-credits />
        <v-alert
          v-if="selectedTemplate && featureEnabled('accountStatus')"
          outlined
          type="info"
        >
          You will be charged <credit-amount :amount="selectedTemplate.creditCost" /> for sending or downloading this document.
        </v-alert>
      </v-col>
    </v-row>
    <v-row>
      <v-col class="pt-0 pt-sm-3 px-xs-only-0">
        <div
          v-show="isIndividual"
          class="fill-height display-flex column align-center justify-center fill-width pos-abs z-2"
        >
          <div class="v-overlay v-overlay--active z-auto" />
          <v-alert
            :value="true"
            class="mb-5 white"
            outlined
            type="info"
          >
            <div class="mt-2">
              Sorry, creating documents is disabled for individuals during this
              beta.<br><br>
              Please speak to your Financial Adviser/Planner if you would like
              to start a new document.
            </div>
            <div class="display-flex row">
              <v-spacer />
              <v-btn
                small
                text
                class="mb-0 mr-0"
                @click="$router.go(-1)"
              >
                Back
              </v-btn>
            </div>
          </v-alert>
        </div>

        <v-card
          v-if="isAdmin && account.shortName === 'OpenLaw'"
          class="fill-height"
        >
          <ol-card-text>
            <v-alert
              :value="true"
              class="white"
              outlined
              type="info"
            >
              You're logged in as an Administrator with 'OpenLaw' as the
              selected account. Go to
              <router-link to="/oladmin/accounts">
                Accounts<!--
           -->
              </router-link>
              to select another account.
            </v-alert>
          </ol-card-text>
        </v-card>

        <template v-else>
          <v-tabs
            v-show="false"
            v-model="activeTab"
            background-color="transparent"
          >
            <v-tab class="justify-start">
              <v-icon
                v-if="!selectedTemplate"
                left
                class="hidden-xs-only"
              >
                mdi-file-document-outline
              </v-icon>
              <v-icon
                v-else
                color="success"
                left
                class="hidden-xs-only"
              >
                mdi-check
              </v-icon>
              Template
            </v-tab>

            <v-tab class="justify-start">
              <v-icon
                v-if="selectedClientObjects.length === 0"
                left
                class="hidden-xs-only"
              >
                mdi-account-group-outline
              </v-icon>
              <v-icon
                v-else
                color="success"
                left
                class="hidden-xs-only"
              >
                mdi-check
              </v-icon>
              {{ showCounterpartyTab ? 'Counterparty' : 'Client(s)' }}
            </v-tab>
            <v-tab
              v-if="!showCounterpartyTab"
              class="justify-start"
            >
              <v-icon
                v-if="selectedJobObjects.length === 0"
                left
                class="hidden-xs-only"
              >
                mdi-format-list-checkbox
              </v-icon>
              <v-icon
                v-else
                color="success"
                left
                class="hidden-xs-only"
              >
                mdi-check
              </v-icon>
              Job
            </v-tab>
          </v-tabs>

          <v-tabs-items
            v-model="activeTab"
            class="transparent new-document-tabs"
          >
            <!-- Templates -->
            <v-tab-item>
              <v-card flat>
                <ol-card-text class="pb-0">
                  <v-row class="align-center justify-right">
                    <v-col
                      cols="6"
                      md="2"
                    >
                      <search-field
                        v-model="templateSearch"
                        label="Search templates"
                      />
                    </v-col>
                    <v-col>
                      <v-btn
                        color="primary"
                        outlined
                        small
                        class="my-doc-upload-btn"
                        @click="showUploadDocumentDialog = true"
                      >
                        <span class="text-left btn-text">UPLOAD FOR SIGNATURE</span>
                        <v-icon class="btn-icon">
                          mdi-upload
                        </v-icon>
                      </v-btn>
                    </v-col>
                  </v-row>

                  <!--                   <div style="display: grid; gap: 16px; grid-template-columns: repeat(auto-fill, minmax(280px, max-content));">
                  </div> -->

                  <v-row wrap>
                    <v-col
                      v-for="category in categories"
                      :key="category.name"
                      cols="3"
                    >
                      <new-document-template-list
                        :title="category.name"
                        :description="category.description"
                        :templates="category.templates"
                        :search="templateSearch"
                        @templateSelected="selectTemplate"
                      />
                    </v-col>
                    <v-col cols="3">
                      <new-document-template-list
                        title="My Templates"
                        :templates="activeAccountTemplates"
                        :search="templateSearch"
                        @templateSelected="selectTemplate"
                      >
                        <new-document-template-category-description>
                          <div v-if="allowAccountTemplates">
                            Contact
                            <support-link subject="Bespoke documents" /> to
                            automate some bespoke documents for your business.
                          </div>
                          <div v-else>
                            Contact support at
                            <support-link subject="Upgrade my account" /> to
                            upgrade your account to have access to custom
                            contract templates.
                          </div>
                        </new-document-template-category-description>
                      </new-document-template-list>
                    </v-col>
                  </v-row>

                  <!-- TODO PENDING TEMPLATES? -->
                </ol-card-text>
              </v-card>
            </v-tab-item>

            <!-- Clients / Counterparty -->
            <v-tab-item>
              <v-card flat>
                <ol-card-title
                  v-if="!showCounterpartyTab"
                  class="pb-0"
                >
                  Select Client(s)
                </ol-card-title>
                <ol-card-title
                  v-else
                  class="pb-0"
                >
                  Select Parties
                </ol-card-title>

                <ol-card-text>
                  <v-row wrap>
                    <v-col
                      :class="
                        $vuetify.breakpoint.mdAndUp ? 'col-8' : 'cols-auto'
                      "
                    >
                      <!-- TODO need a super small breakpoint for full width at small screen size -->
                      <!-- TODO single select for now -->
                      <!-- TODO implement proper pagination (jobs too) -->
                      <!-- TODO need recent interactions, not just user modified  -->
                      <!-- TODO mobile view is yuck -->
                      <!-- Clients Table -->
                      <v-data-table
                        v-if="!showCounterpartyTab"
                        v-model="selectedClientObjects"
                        :headers="clientHeaders"
                        :items="clientUsersMapped"
                        item-key=".key"
                        :search="clientSearch"
                        :loading="clientUsersLoading"
                        :sort-by="['recent', 'name']"
                        :sort-desc="[false, false]"
                        show-select
                        single-select
                        :disable-pagination="clientsExpanded"
                        :items-per-page="clientItemsPerPage"
                        font="-1"
                        class="selectable-rows"
                      >
                        <template #top>
                          <v-row class="align-center justify-space-between">
                            <v-col
                              cols="10"
                              class="content-col-sm-5 content-col-md-4 content-col-lg-3 content-col-xl-2"
                            >
                              <ol-text-field
                                v-model="clientSearch"
                                label="Search"
                                dense
                                hide-details
                                clearable
                                prepend-inner-icon="mdi-magnify"
                                autofocus
                              />
                            </v-col>

                            <v-col
                              cols="2"
                              class="offset-sm-1 content-col-sm-3 content-col-md-auto text-right pl-0"
                            >
                              <!-- TODO ol-btn? -->

                              <!-- TODO create self-contained dialog -->
                              <ol-tooltip>
                                <template #[`activator`]="{ on }">
                                  <add-new-button
                                    target="_blank"
                                    to="/clients"
                                    v-on="on"
                                  />
                                </template>
                                Add new client
                              </ol-tooltip>
                            </v-col>
                          </v-row>
                        </template>
                        <!-- Hide Select-All -->
                        <template #[`header.data-table-select`]="{}">
                          <div style="width: 40px" />
                        </template>

                        <template #[`item.isRecent`]="{ item }">
                          {{ item.isRecent ? 'Recent' : '' }}
                        </template>

                        <template #footer>
                          <v-btn
                            color="primary"
                            outlined
                            :disabled="
                              clientUsersMapped.length <= clientItemsPerPage
                            "
                            small
                            @click="clientsExpanded = !clientsExpanded"
                          >
                            {{ !clientsExpanded ? 'See all' : 'See less' }}
                          </v-btn>
                        </template>
                      </v-data-table>

                      <!-- Select first party -->
                      <div v-if="showCounterpartyTab">
                        <h2>First party signatory</h2>
                        <signatory-select
                          :selected-account="firstPartyAccountId"
                          :selected-signatories="firstPartySignatories"
                          @change="selectFirstParty"
                        />

                        <v-divider style="margin: 30px 0px" />

                        <h2>Counter Parties</h2>
                      </div>

                      <!-- Counterparty Table -->
                      <v-data-table
                        v-if="showCounterpartyTab"
                        v-model="selectedCounterParties"
                        :headers="counterpartyHeaders"
                        :items="counterpartyUsersMapped"
                        item-key=".key"
                        :search="counterpartySearch"
                        :loading="clientUsersLoading"
                        :sort-by="['recent', 'name']"
                        :sort-desc="[false, false]"
                        show-select
                        class="selectable-rows"
                        @click:row="
                          (item, data) => {
                            data.select(!data.isSelected)
                          }
                        "
                      >
                        <template #top>
                          <v-row class="align-center justify-space-between">
                            <v-col
                              cols="6"
                              md="5"
                            >
                              <search-field
                                v-model="counterpartySearch"
                                label="Search counterparties"
                              />
                            </v-col>

                            <v-col
                              cols="2"
                              class="text-right"
                            >
                              <!-- TODO ol-btn? -->

                              <!-- TODO create self-contained dialog -->
                              <ol-tooltip>
                                <template #[`activator`]="{}">
                                  <add-new-button
                                    fab
                                    @click="addCounterpartyDialog = true"
                                  />
                                </template>
                                Add counterparty
                              </ol-tooltip>
                            </v-col>
                          </v-row>
                        </template>
                        <!-- Hide Select-All -->
                        <template #[`header.data-table-select`]="{}">
                          <div style="width: 40px" />
                        </template>

                        <template #[`item.isRecent`]="{ item }">
                          {{ item.isRecent ? 'Recent' : '' }}
                        </template>
                      </v-data-table>
                    </v-col>
                  </v-row>
                  <v-row>
                    <v-col
                      md="4"
                      lg="3"
                    >
                      <h2>Other notifications</h2>
                      <v-text-field
                        v-model="notifyOnCompletion"
                        hint="Is there anyone else you wish to notify once this document is complete? Enter their email address."
                        label="Notify on completion"
                      />
                    </v-col>
                  </v-row>

                  <!-- TODO PENDING CLIENTS -->
                </ol-card-text>
              </v-card>
            </v-tab-item>

            <!-- Jobs -->
            <v-tab-item>
              <v-card flat>
                <ol-card-title class="pb-0">
                  Select Job
                </ol-card-title>

                <ol-card-text>
                  <v-row wrap>
                    <v-col
                      cols="auto"
                      class="pt-0"
                    >
                      <!-- TODO single select for now -->
                      <!-- TODO add client(s) to existing job? -->
                      <v-data-table
                        v-model="selectedJobObjects"
                        :headers="jobHeaders"
                        :items="jobsArray"
                        item-key="jobId"
                        :search="jobSearch"
                        :loading="jobsLoading"
                        :sort-by="['clientsSortKey', 'isRecent']"
                        :sort-desc="[false, true]"
                        :selectable-key="
                          selectedClientObjects.length > 0
                            ? 'isSelectedClientJob'
                            : ''
                        "
                        show-select
                        single-select
                        class="selectable-rows"
                        :disable-pagination="jobsExpanded2"
                        :items-per-page="jobItemsPerPage"
                        font="-1"
                        :class="{
                          'hide-colgroup table-fixed': $vuetify.breakpoint.xs
                        }"
                      >
                        <template #top>
                          <v-row class="align-center justify-space-between">
                            <v-col
                              cols="10"
                              class="content-col-sm-5 content-col-md-4 content-col-lg-3 content-col-xl-2"
                            >
                              <ol-text-field
                                v-model="jobSearch"
                                label="Search"
                                dense
                                hide-details
                                clearable
                                prepend-inner-icon="mdi-magnify"
                                autofocus
                              />
                            </v-col>

                            <v-col
                              cols="2"
                              class="offset-sm-1 content-col-sm-3 content-col-md-auto text-right pl-0"
                            >
                              <!-- TODO ol-btn? -->

                              <ol-tooltip>
                                <template #[`activator`]="{ on }">
                                  <add-new-button
                                    v-on="on"
                                    @click="openNewJobDialog(newJob)"
                                  />
                                </template>
                                Create new job
                              </ol-tooltip>
                            </v-col>
                          </v-row>
                        </template>

                        <!-- Hide Select-All -->
                        <template #[`header.data-table-select`]="{}">
                          <div style="width: 40px" />
                        </template>

                        <template #[`item.clientsSortKey`]="{ item }">
                          <template
                            v-for="(client, index) in item.clientDetails"
                          >
                            <span
                              v-show="index > 0"
                              :key="index"
                            >, </span>
                            <span
                              :key="index"
                              :class="{
                                'font-weight-bold':
                                  item.isSelectedClientJob &&
                                  client.id === selectedClientObjects[0].id
                              }"
                            >
                              {{
                                client && client.name ? client.name : ''
                              }}</span>
                          </template>
                        </template>

                        <template #[`item.isSelectedClientJob`]="{ item }">
                          {{
                            item.isSelectedClientJob
                              ? 'Y'
                              : selectedClientObjects.length
                                ? 'N'
                                : 'N/A'
                          }}
                        </template>

                        <template #[`item.isRecent`]="{ item }">
                          {{ item.isRecent ? 'Recent' : '' }}
                          <!-- {{item.modified.toDate()}} -->
                        </template>

                        <template #footer>
                          <v-btn
                            :disabled="jobsArray.length <= jobItemsPerPage"
                            small
                            text
                            @click="jobsExpanded2 = !jobsExpanded2"
                          >
                            {{ !jobsExpanded2 ? 'See all' : 'See less' }}
                          </v-btn>
                        </template>
                      </v-data-table>
                    </v-col>
                  </v-row>

                  <v-expansion-panels
                    v-if="false"
                    v-model="filteredJobsExpanded"
                    class="mt-2"
                    multiple
                  >
                    <v-expansion-panel
                      v-for="group in jobsGrouped"
                      :key="group.order"
                    >
                      <v-expansion-panel-header>
                        <span :class="{ 'primary--text': group.hasSelected }">{{
                          group.groupName
                        }}</span>
                      </v-expansion-panel-header>
                      <v-expansion-panel-content eager>
                        <v-data-iterator
                          v-model="selectedJobObjects"
                          :search="jobSearch"
                          hide-default-footer
                          :loading="jobsLoading"
                          single-select
                          :items="group.jobs"
                          :no-data-text="
                            group.noDataText || 'No data available'
                          "
                          item-key="jobId"
                          @current-items="
                            (v) => currentJobItems(v, group.groupName)
                          "
                        >
                          <template
                            #[`default`]="{ items, isSelected, select }"
                          >
                            <v-row>
                              <v-col
                                v-for="job in items"
                                :key="job.jobId"
                                cols="12"
                                class="content-col-sm-6 content-col-md-4 content-col-lg-3"
                              >
                                <v-card
                                  :color="isSelected(job) ? 'primary' : ''"
                                  class="mx-auto fill-height"
                                  flat
                                  style="border-radius: 8px"
                                  @click="
                                    (v) => {
                                      select(job, isSelected(job) ? false : v)
                                      if (isSelected(job)) {
                                        openNewJobDialog(job)
                                      }
                                    }
                                  "
                                >
                                  <v-list-item
                                    three-line
                                    dense
                                    class="pr-0 fill-height"
                                  >
                                    <v-list-item-content
                                      class="align-content-space-between mr-2 fill-height"
                                    >
                                      <div class="container--fluid">
                                        <v-list-item-title
                                          class="mb-1 overflow-visible text-wrap"
                                        >
                                          <div
                                            class="d-flex justify-space-between align-center"
                                          >
                                            <div
                                              class="body-2 mr-1"
                                              style="flex: 1 0 1%"
                                            >
                                              {{ job.jobName }}
                                            </div>
                                          </div>
                                        </v-list-item-title>
                                      </div>
                                      <span>{{
                                        job.clientDetails
                                          .map((user) => user.name)
                                          .join(', ')
                                      }}</span>
                                    </v-list-item-content>
                                    <!-- TODO <v-tooltip> -->

                                    <v-list-item-avatar
                                      tile
                                      size="24"
                                      :color="
                                        toMaterialStyle(job.jobId, 400)
                                          .backgroundColor
                                      "
                                      class="body-2 ma-0 py-1 px-1"
                                      style="
                                        max-width: 36px;
                                        width: auto;
                                        line-height: normal;
                                        text-orientation: sideways;
                                        writing-mode: vertical-rl;
                                        height: auto;
                                        min-height: 76px;
                                        max-height: 100px;
                                      "
                                    >
                                      <span class="small fill-height">{{
                                        job.jobId
                                      }}</span>
                                      <!--                                       <span class="small fill-height" :style="'color: ' + toMaterialStyle(job.jobId, 300).color">{{ job.jobId }}</span> -->
                                    </v-list-item-avatar>
                                  </v-list-item>
                                  <!-- <v-icon v-show="job.modified && (mountTime - job.modified.toDate() < millisecondsPerDay)" style="position: absolute; left: 0; bottom: 0;" color="primary">
                                    mdi-new-box
                                  </v-icon> -->

                                  <v-avatar
                                    v-show="
                                      job.modified &&
                                        mountTime - job.modified.toDate() <
                                        1000 * 60 * 60
                                    "
                                    style="
                                      position: absolute;
                                      bottom: 0;
                                      border-radius: 0;
                                    "
                                    color="primary"
                                    size="20"
                                    title="New"
                                    tile
                                  >
                                    <v-icon x-small>
                                      mdi-asterisk
                                    </v-icon>
                                  </v-avatar>
                                </v-card>
                              </v-col>
                            </v-row>
                          </template>
                        </v-data-iterator>
                      </v-expansion-panel-content>
                    </v-expansion-panel>
                  </v-expansion-panels>
                </ol-card-text>
              </v-card>
            </v-tab-item>
          </v-tabs-items>

          <v-footer
            fixed
            inset
            app
            class="pb-2 flex-wrap flex-sm-nowrap"
          >
            <div class="caption">
              <!-- <div class="caption col-12 content-col-sm-auto flex-sm-shrink-1 px-xs-only-0 pt-xs-only-0 pb-xs-only-1" style="max-width:250px;"> -->
              <div class="">
                <span>{{ createTextObj.templateText }}</span><a
                  class="darkslategray--text font-weight-bold"
                  @click="activeTab = 0"
                >{{
                  (selectedTemplate && selectedTemplate.name) ||
                    '&lt;select template...&gt;'
                }}</a>
                <span v-show="createTextObj.lawyerText">{{
                  createTextObj.lawyerText
                }}</span><wbr>
                <span v-show="createTextObj.clientText"><span>{{ createTextObj.clientText }}</span><a
                  class="darkslategray--text font-weight-bold"
                  @click="activeTab = 1"
                >{{
                  (selectedClientObjects[0] &&
                    selectedClientObjects[0].name) ||
                    '&lt;select client(s)...&gt;'
                }}</a></span>
                <span v-show="createTextObj.counterpartyText"><span>{{ createTextObj.counterpartyText }}</span><a
                  class="lightseagreen--text font-weight-bold"
                  @click="activeTab = 1"
                >{{
                  (selectedCounterParties[0] &&
                    selectedCounterParties[0].name) ||
                    '&lt;select counterparty(s)...&gt;'
                }}</a></span>
                <span v-show="createTextObj.jobText"><span>{{ createTextObj.jobText }}</span><a
                  class="primary--text"
                  @click="activeTab = 2"
                >{{
                  (selectedJobObjects[0] &&
                    selectedJobObjects[0].jobName +
                    ' (' +
                    selectedJobObjects[0].jobId +
                    ')') ||
                    '&lt;select job...&gt;'
                }}</a></span>.
              </div>
            </div>
            <v-spacer />

            <div style="flex: 1 1 auto; text-align: right">
              <v-btn
                v-if="activeTab < tabs - 1 && !canCreate"
                :small="$vuetify.breakpoint.xs"
                color="primary"
                @click="activeTab++"
              >
                Next
              </v-btn>

              <v-btn
                v-else
                :disabled="!canCreate"
                color="primary"
                :loading="creating"
                @click="createNewDocument"
              >
                Create
              </v-btn>
            </div>
          </v-footer>
        </template>
      </v-col>
    </v-row>

    <new-job-dialog
      :new-job-dialog="newJobDialog"
      :p-new-job-id="newJobId"
      :p-new-job-name="newJobName"
      :selected-job-objects="selectedJobObjects"
      :selected-client-objects="selectedClientObjects"
      @close="closeNewJobDialog"
    />

    <v-dialog
      v-model="showUploadDocumentDialog"
      max-width="500px"
      scrollable
      @keydown.esc="showUploadDocumentDialog = false"
    >
      <document-upload @close="showUploadDocumentDialog = false" />
    </v-dialog>

    <v-dialog
      v-model="addCounterpartyDialog"
      persistent
      max-width="500px"
    >
      <add-counterparty-form
        @cancel="addCounterpartyDialog = false"
        @counterpartyAdded="addCounterpartyDialog = false"
      />
    </v-dialog>
  </v-container>
</template>

<script>
import { fsdb, auth } from '@/firebase'
import toMaterialStyle from '@/utils/toMaterialStyle'
import {
  isSingleUserAccount,
  isStandardBusinessAccount
} from '@/dataAccess/accountTypes'

import orderBy from 'lodash/orderBy'

import { createCounterPartiesData } from '@/dataAccess/parties'

import AddNewButton from '@/components/AddNewButton'
import SearchField from '@/components/SearchField'
import { buildTemplatesByCategory } from '@/utils/buildTemplatesByCategory'
import NewDocumentTemplateList from '@/components/NewDocumentTemplateList'
import NewDocumentTemplateCategoryDescription from '@/components/NewDocumentTemplateCategoryDescription'
import SupportLink from '@/components/SupportLink'
import SignatorySelect from '@/components/SignatorySelect'
import TemplatePreviewDialog from '@/components/TemplatePreviewDialog'
import AccountCredits from '@/components/AccountCredits'
import CreditAmount from '@/components/CreditAmount'

const NewJobDialog = () => import('@/components/NewJobDialog')
const DocumentUpload = () => import('@/components/DocumentUpload')
const AddCounterpartyForm = () => import('@/components/AddCounterpartyForm')

// TODO BETA review lists/dialogs to ensure only active users can be selected - what should happen when a user is deactivated?

// const millisecondsPerDay = 86400000

export default {
  name: 'NewDocument',
  components: {
    AccountCredits,
    AddNewButton,
    AddCounterpartyForm,
    CreditAmount,
    DocumentUpload,
    NewDocumentTemplateCategoryDescription,
    NewDocumentTemplateList,
    NewJobDialog,
    SearchField,
    SignatorySelect,
    SupportLink,
    TemplatePreviewDialog
  },
  data: () => ({
    activeTab: 0,
    clientsExpanded: false,
    jobsExpanded: [],
    jobsExpanded2: false,
    currentJobItemCounts: {},
    clientUsersLoading: true,
    pendingClientUsersLoading: true,
    counterpartyUsersLoading: true,
    jobsLoading: true,
    selectedTemplate: null,
    workflow: 'self',
    selectedClientObjects: [],
    selectedCounterParties: [],
    selectedJobObjects: [],
    templateSearch: '',
    isFirstPartySelf: true,
    clientSearch: '',
    counterpartySearch: '',
    jobSearch: '',
    templates: [],
    jobItemsPerPage: 10,
    clientItemsPerPage: 10,
    newJobDialog: false,
    newJobId: '',
    newJobName: '',
    newJobIdDirty: false,
    newJobNameDirty: false,
    showUploadDocumentDialog: false,
    addCounterpartyDialog: false,
    firstPartyAccountId: null,
    firstPartySignatories: [],
    creating: false,
    notifyOnCompletion: null
  }),
  computed: {
    allowAccountTemplates() {
      return this.isStandardBusiness
    },
    showClientList() {
      return this.appUserLoaded && !this.isIndividual
    },
    showJobList() {
      return this.appUserLoaded && !this.isIndividual
    },
    canCreate() {
      if (this.isIndividual) {
        return !!this.selectedTemplate // TODO auto job
      }

      if (!this.isSingleUser && !this.isStandardBusiness) {
        return (
          !!this.selectedTemplate &&
          !!this.selectedClientObjects[0] &&
          this.selectedJobObjects[0] &&
          this.selectedJobObjects[0].jobName !== 'New Job'
        )
      }

      return (
        this.selectedTemplate &&
        this.selectedCounterParties[0] &&
        this.firstPartySignatories.length > 0
      )
    },
    createTextObj() {
      var obj = {}
      obj.templateText = 'Create '
      if (this.isIndividual) {
        if (this.selectedTemplate && this.workflow === 'lawyer') {
          obj.lawyerText = ' assisted by a lawyer'
        }
        return obj
      } else if (this.isSingleUser || this.isStandardBusiness) {
        obj.counterpartyText = ' on behalf of '
        return obj
      }

      /* if this.business */

      obj.clientText = ' on behalf of '
      obj.jobText = ' in job '
      return obj
    },
    categories() {
      if (this.userTemplatesLoading) {
        return []
      }
      return buildTemplatesByCategory({
        categories: this.userTemplateCategories,
        templates: this.userTemplates
      })
    },
    counterpartyUsersMapped() {
      return orderBy(
        this.counterpartyUsers.map((cpUser) => {
          const minus3Months = ((now) => now.setMonth(now.getMonth() - 3))(
            new Date()
          )

          return {
            ...cpUser,
            initials:
              cpUser.initials ||
              cpUser.name.split(' ').reduce((response, word) => {
                response += word.slice(0, 1)
                return response
              }, ''),
            isRecent:
              cpUser.modified && cpUser.modified.toDate() >= minus3Months
          }
        }),
        'name'
      )
    },
    clientUsersMapped() {
      // TODO move initials to trigger
      return orderBy(
        this.clientUsers.map((client) => {
          const minus3Months = ((now) => now.setMonth(now.getMonth() - 3))(
            new Date()
          )

          return {
            ...client,
            initials:
              client.initials ||
              client.name.split(' ').reduce((response, word) => {
                response += word.slice(0, 1)
                return response
              }, ''),
            isRecent:
              client.modified && client.modified.toDate() >= minus3Months
          }
        }),
        'name'
      )
    },
    clientUsersMappedById() {
      return Object.fromEntries(
        this.clientUsersMapped.map((user) => [user.id, user])
      )
    },
    allJobsExpanded() {
      return this.jobsGrouped
        .map((item, index) => (item.closed ? null : index))
        .filter((index) => index != null)
    },
    filteredJobsExpanded() {
      return this.jobsGrouped
        .map((group) => [
          group.groupName,
          this.currentJobItemCounts[group.groupName]
        ])
        .map(([group, count], index) => [group, count, index])
        .filter(([group, count, index]) => count > 0)
        .map(([group, count, index]) => index)
    },
    recentClientUsersMapped() {
      // TODO test this works then add Expansion panels
      const recentJobsGroup = this.jobsGrouped.find(
        (group) => group.groupName === 'All Recent Jobs'
      )
      if (!recentJobsGroup.jobs) {
        return []
      }
      const recentClientIds = recentJobsGroup.jobs
        .map((job) => job.clients)
        .flat()
      return this.clientUsersMapped.filter((clientUser) =>
        recentClientIds.includes(clientUser.id)
      )
    },
    jobsArray() {
      if (this.jobsLoading) {
        return []
      }
      var jobsArray = Object.entries(this.jobs)
        .filter(([jobId, job]) => jobId !== '.key')
        .map(([jobId, job]) => {
          const clientDetails = orderBy(
            job.clients.map((clientId) => this.clientUsersMappedById[clientId]),
            'name'
          )

          const minus3Months = ((now) => now.setMonth(now.getMonth() - 3))(
            new Date()
          )

          var jobObj = {
            jobId: jobId,
            ...job,
            accountId: this.$store.state.account.id,
            clientDetails: clientDetails,
            isSelectedClientJob:
              (this.selectedClientObjects.length &&
                job.clients.includes(this.selectedClientObjects[0].id)) ||
              false,
            isRecent: job.modified && job.modified.toDate() >= minus3Months
          }

          var clientNamesStr = ''
          if (clientDetails[0]) {
            clientNamesStr = clientDetails
              .map((client) => client.name)
              .join(',')
          }
          jobObj.clientsSortKey =
            (jobObj.isSelectedClientJob ? '0-' : '1-') + clientNamesStr

          return { ...jobObj, searchStr: JSON.stringify(jobObj) }
        })

      /* TODO remove when table */
      return orderBy(jobsArray, 'jobName')
    },
    newJob() {
      if (
        !this.selectedClientObjects ||
        this.selectedClientObjects.length === 0
      ) {
        return {}
      }

      const newJob = {
        accountId: this.$store.state.account.id,
        clients: this.selectedClientObjects.map((user) => user.id)
      }

      newJob.clientDetails = orderBy(
        newJob.clients.map((clientId) => this.clientUsersMappedById[clientId]),
        'name'
      )

      // create jobId from client(s) initials and yyyy-mm
      // newJob.jobId = newJob.clientDetails.map(user => user.initials).join('/') + '/' + this.$toLocaleYYYYMMDDsH_MMpString(new Date()).slice(0, 7)
      // create jobName from client(s) last name (last word in name) and template name
      // newJob.jobName = Array.from(new Set(newJob.clientDetails.map(user => user.name.split(' ').pop()))).join(', ') + ' ' + (this.selectedTemplates.length > 0 ? this.selectedTemplates[0].name : '<select\xa0template>')
      newJob.jobId = 'xx/xxxx-xx'
      newJob.jobName = 'New Job'

      return newJob
    },
    jobsGrouped() {
      var groups = []

      if (this.selectedClientObjects.length > 0) {
        const newJob = {
          accountId: this.$store.state.account.id,
          clients: this.selectedClientObjects.map((user) => user.id)
        }

        newJob.clientDetails = orderBy(
          newJob.clients.map(
            (clientId) => this.clientUsersMappedById[clientId]
          ),
          'name'
        )

        // create jobId from client(s) initials and yyyy-mm
        // newJob.jobId = newJob.clientDetails.map(user => user.initials).join('/') + '/' + this.$toLocaleYYYYMMDDsH_MMpString(new Date()).slice(0, 7)
        // create jobName from client(s) last name (last word in name) and template name
        // newJob.jobName = Array.from(new Set(newJob.clientDetails.map(user => user.name.split(' ').pop()))).join(', ') + ' ' + (this.selectedTemplates.length > 0 ? this.selectedTemplates[0].name : '<select\xa0template>')
        newJob.jobId = 'xx/xxxx-xx'
        newJob.jobName = 'New Job'

        groups.push({
          groupName: 'New Job',
          jobs: [newJob],
          order: groups.length + 1,
          hasSelected: false
        })

        groups.push({
          groupName: this.selectedClientObjects[0].name + "'s Jobs",
          jobs: this.jobsArray.filter((job) =>
            job.clients.includes(this.selectedClientObjects[0].id)
          ),
          order: groups.length + 1,
          hasSelected: false
        })
      } else {
        groups.push({
          groupName: 'New Job - No client(s) selected',
          jobs: [],
          order: groups.length + 1,
          hasSelected: false,
          noDataText: 'Select one or more clients',
          closed: true
        })

        groups.push({
          groupName: 'Client Jobs - No client(s) selected',
          jobs: [],
          order: groups.length + 1,
          hasSelected: false,
          noDataText: 'Select one or more clients',
          closed: true
        })
      }

      /* groups.push({
          groupName: this.selectedClientObjects[0].name + '\'s Jobs',
          jobs: this.selectedClientObjects.length > 0 ? this.jobsArray.filter(job => job.clients.includes(this.selectedClientObjects[0].id)) || [],
          order: 1,
          hasSelected: false
        })       */

      const minus3Months = ((now) => now.setMonth(now.getMonth() - 3))(
        new Date()
      )

      groups.push({
        groupName: 'All Recent Jobs',
        jobs: this.jobsArray.filter(
          (job) => job.modified && job.modified.toDate() >= minus3Months
        ),
        order: groups.length + 1,
        hasSelected: false
      })

      groups.push({
        groupName: 'All Jobs',
        jobs: this.jobsArray,
        order: groups.length + 1,
        hasSelected: false
      })

      if (this.selectedJobObjects.length > 0) {
        groups.forEach((group) => {
          group.hasSelected = group.jobs.some(
            (job) => job.jobId === this.selectedJobObjects[0].jobId
          )
        })
      }

      return groups
    },
    hasSearchParam() {
      return !!this.$route.query.q
    },
    recentJobIds() {
      if (!this.jobsArray.length) {
        return []
      }

      return orderBy(
        this.jobsArray
          .filter((job) => job.isRecent)
          .map((job) => job.jobId)
          .sort()
      )
    },
    nextJobId() {
      const autoJobIds = this.recentJobIds.filter((jobId) =>
        /\d\d\d\d-\d+/.test(jobId)
      )
      const lastJobId = autoJobIds[autoJobIds.length - 1]
      const currentYear = new Date().getFullYear()
      var newJobNum = '001'
      if (lastJobId) {
        const [year, jobNum] = lastJobId.split('-')
        if (+year === currentYear) {
          newJobNum = ('' + (+jobNum + 1)).padStart(3, '0')
        }
      }
      return currentYear + '-' + newJobNum
    },
    jobIdsToName() {
      if (!this.documents.length) {
        return {}
      }
      return this.documents.reduce(
        (acc, doc) => ({ ...acc, [doc.jobId]: doc.jobName }),
        {}
      )
    },
    jobNamesToId() {
      if (!this.documents.length) {
        return {}
      }
      return this.documents.reduce(
        (acc, doc) => ({ ...acc, [doc.jobName]: doc.jobId }),
        {}
      )
    },
    clientJobMismatch() {
      if (this.selectedClientObjects.length > 0) {
        if (this.selectedJobObjects.length > 0) {
          return this.selectedClientObjects.some(
            (client) => !this.selectedJobObjects[0].clients.includes(client.id)
          )
          /*             if (this.selectedClientObjects.some(client => !this.selectedJobObjects[0].clients.includes(client.id))) {
              // TODO show warning if client not already in job
              this.$showError('Selected job does not include selected client(s).')
            } */
        }
      }
      return false
    },
    newJobIdValid() {
      return this.newJobId.length > 0
    },
    newJobNameValid() {
      return this.newJobName.length > 0
    },
    newJobValid() {
      return this.newJobIdValid && this.newJobNameValid
    },
    templateHeaders() {
      return [{ text: '', value: 'name', width: '100%' }]
    },
    counterpartyHeaders() {
      return [
        { text: '', value: 'data-table-select', align: ' pr-0' },
        { text: 'Name', value: 'name' },
        { text: 'Account', value: 'accountShortName' },
        { text: 'Email', value: 'email' },
        { text: 'Recent', value: 'isRecent' }
      ]
    },
    clientHeaders() {
      return [
        { text: '', value: 'data-table-select', align: ' pr-0' },
        { text: 'Name', value: 'name', align: ' text-no-wrap px-0' },
        /* hidden but searchable */
        /* { text: 'id', value: 'id', align: ' d-none' }, */

        { text: 'Email', value: 'email', align: ' text-break text-sm-no-wrap' },
        { text: 'Recent', value: 'isRecent' }
      ]
    },
    jobHeaders() {
      return [
        { text: '', value: 'data-table-select', align: ' pr-0' },
        { text: 'JobId', value: 'jobId', align: ' text-no-wrap px-0' },
        { text: 'Job Name', value: 'jobName', align: ' pr-o' },

        /* hidden but searchable */
        { text: 'SearchStr', value: 'searchStr', align: ' d-none' },

        { text: 'Client(s)', value: 'clientsSortKey', align: ' ' },
        { text: 'Recent', value: 'isRecent' } /* ,
        { text: 'Email', value: 'email', align: ' ' } */
      ]
    },
    isSingleUser() {
      return isSingleUserAccount(this.account.type)
    },
    isStandardBusiness() {
      return isStandardBusinessAccount(this.account.type)
    },
    tabs() {
      return this.isSingleUser || this.isStandardBusiness ? 2 : 3
    },
    showCounterpartyTab() {
      return this.isSingleUser || this.isStandardBusiness
    }
  },
  watch: {
    account: {
      handler: function (newVal, oldVal) {
        if ('id' in newVal) {
          this.$binding(
            'jobs',
            fsdb.collection('accountJobs').doc(this.$store.state.account.id)
          )
            .then(() => {
              this.jobsLoading = false
            })
            .catch((err) => {
              if (err.message.includes('permission denied')) {
                this.jobsLoading = false
                // just means account doesn't have jobs yet
                return
              }
              // TODO sentry log
              return err
            })
        }
      },
      deep: true,
      immediate: true
    },
    allJobsExpanded: {
      handler: function (newVal, oldVal) {
        this.jobsExpanded = this.allJobsExpanded.filter(
          (jobIndex) =>
            jobIndex === 0 ||
            (this.jobsGrouped[jobIndex] &&
              this.jobsGrouped[jobIndex].jobs.length > 0)
        )
      },
      deep: true
    },
    selectedJobObjects: {
      handler: function (newVal, oldVal) {
        if (this.selectedJobObjects.length > 0) {
          // if it's a single client job, select that client. Convenience.
          if (!(this.selectedClientObjects.length > 0)) {
            const jobClients = this.selectedJobObjects[0].clients
            if (jobClients.length === 1) {
              this.selectedClientObjects.push(
                this.clientUsersMapped.find(
                  (clientUser) => clientUser.id === jobClients[0]
                )
              )
            }
          }
        }
      },
      deep: true
    },
    clientJobMismatch: function (newVal, oldVal) {
      if (this.clientJobMismatch) {
        // TODO show warning if client not already in job
        if (this.selectedJobObjects[0].name === 'New Job') {
        } else {
          this.$showErrorbar(
            'Selected job does not include selected client(s).'
          ) // TODO "To add a client to an existing job..."
          this.selectedJobObjects.pop()
        }
      }
    },
    jobs: {
      handler: function (newVal, oldVal) {
        if (this.newJobId in this.jobs) {
          // if the new newJobId job has been loaded, select it
          this.$set(
            this.selectedJobObjects,
            0,
            this.jobsArray.find((job) => job.jobId === this.newJobId)
          )
        }
      },
      deep: true
    }
  },
  async beforeCreate() {
    this.toMaterialStyle = toMaterialStyle
    this.mountTime = new Date()
    // this.millisecondsPerDay = millisecondsPerDay

    this.mapStates([
      'account',
      'activeAccountTemplates',
      'appUser',
      'clientUsers',
      'counterpartyUsers',
      'documents',
      'documentsLoading',
      'pendingClientUsers',
      'userTemplateCategories',
      'userTemplates',
      'userTemplatesLoading'
    ])
    this.mapGetters(['appUserLoaded', 'isIndividual', 'isAdmin', 'usersById', 'featureEnabled'])
    this.$store.dispatch('get_counterpartyUsers').then(() => {
      this.counterpartyUsersLoading = false
    })
    this.$store.dispatch('get_appUser').then((value) => {
      this.firstPartySignatories = [{
        id: this.appUser.id,
        name: this.appUser.name
      }]
      this.firstPartyAccountId = this.appUser.accountId
    })
    this.$store.dispatch('get_clientUsers').then((value) => {
      this.clientUsersLoading = false
    })
    this.$store.dispatch('get_pendingClientUsers').then((value) => {
      this.pendingClientUsersLoading = false
    })
    this.$store.dispatch('get_userTemplates')
    this.$store.dispatch('get_activeAccountTemplates')
  },
  async mounted() {
    // TODO should be watchers
    this.jobsExpanded = this.allJobsExpanded

    // this.pinnedTemplatesRef = fsdb.collection('pinnedTemplates').doc(auth.currentUser.uid)

    this.templateSearch = this.$route.query.q
  },
  methods: {
    nextTab: function () {
      document.activeElement.blur()
      this.activeTab++
    },
    selectTemplate(template) {
      if (template.preview) {
        this.$refs.templatePreview.open(template)
      } else {
        this.selectedTemplate = template
        this.nextTab()
      }
    },
    openNewJobDialog: function (job) {
      // open new job dialog when New Job selected
      if (job.jobName === 'New Job') {
        if (!this.newJobNameDirty || !this.newJobName) {
          this.newJobName =
            this.selectedClientObjects
              .map((clientUser) => clientUser.name)
              .sort()
              .join(', ') + "'s "
          if (this.selectedTemplate) {
            this.newJobName += this.selectedTemplate.name
          } else {
            this.newJobName += '<Template>'
          }
        }
        if (!this.newJobIdDirty || !this.newJobId) {
          let currYear = new Date().getFullYear()
          let jobIdsRegex = new RegExp('.*(20\\d\\d-\\d\\d\\d)')
          let jobIds = this.jobsArray
            .map((job) => job.jobId.replace(jobIdsRegex, '$1'))
            .sort()

          if (jobIds.length > 0 && !!jobIds[0]) {
            let lastJobId = jobIds.pop()
            let lastJobIdNum = Number(lastJobId.replace(/.*-(\d\d\d)/, '$1'))
            lastJobIdNum++
            this.newJobId =
              currYear + '-' + lastJobIdNum.toString().padStart(3, '0')
          } else {
            this.newJobId = currYear + '-001'
          }
        }
        this.newJobDialog = true
      } else {
        this.$showInfobar(
          'Select one or more clients before creating a new job.'
        )
      }
    },
    closeNewJobDialog(success) {
      if (success) {
      }

      this.newJobDialog = false
    },
    currentJobItems(items, group) {
      this.$set(this.currentJobItemCounts, group, items.length)
    },
    selectFirstParty({ accountId, signatories }) {
      this.firstPartyAccountId = accountId
      this.firstPartySignatories = signatories
    },
    async createNewDocument() {
      try {
        this.creating = true
        const templateId = this.selectedTemplate['.key']
        const notifyOnCompletion = this.notifyOnCompletion
          ? [this.notifyOnCompletion]
          : []

        const {
          data: { documentId }
        } = await this.$firePost('documents/createFromTemplate', auth, {
          templateId,
          counterParties: createCounterPartiesData(this.selectedCounterParties),
          firstParty: {
            accountId: this.firstPartyAccountId,
            signatoryIds: this.firstPartySignatories.map(({ id }) => id)
          },
          notifyOnCompletion
        })
        this.$router.push({
          name: 'documents',
          params: { documentId, new: 'new' }
        })
      } catch (e) {
        if ('response' in e) {
          this.$showErrorbar(
            e.response.data.error || JSON.stringify(e.response.data)
          )
        } else {
          this.$showErrorbar(e.message || e)
        }
        this.$Sentry.captureException(e)
      } finally {
        this.creating = false
      }
    }
  }
}
</script>

<style lang="scss" scoped>
.v-tabs-items.new-document-tabs {
  padding-bottom: 2rem;
}
.v-footer {
  background-color: var(--v-lavendergray-base);
  min-height: 80px;
}
</style>
